import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Ic } from './icons';

/* ─────────────────────────────────────────────────────────────
   ERPForce PDF Builder — code editor + field tree
   Exposes export const PBEdit = { CodeEditor, FieldTree }
   ───────────────────────────────────────────────────────────── */

  const { useRef, useEffect, useState } = React;
  

  // ── handlebars + HTML syntax highlight ───────────────────
  function highlight(code) {
    let s = code
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    // html comments
    s = s.replace(/(&lt;!--[\s\S]*?--&gt;)/g, '<span class="tk-com">$1</span>');
    // handlebars mustaches  {{ ... }}
    s = s.replace(/(\{\{[^}]*\}\})/g, (m) => {
      const cls = /\{\{\s*[#\/]/.test(m) || /\{\{\s*else\s*\}\}/.test(m) ? 'tk-blk' : 'tk-mus';
      return `<span class="${cls}">${m}</span>`;
    });
    // tags: &lt;tag ... &gt;
    s = s.replace(/(&lt;\/?)([a-zA-Z][a-zA-Z0-9-]*)([^&]*?)(\/?&gt;)/g, (m, open, name, attrs, close) => {
      const a = attrs.replace(/([a-zA-Z-]+)=(&quot;|")([^"]*)(\2)/g, '<span class="tk-attr">$1</span>=<span class="tk-str">"$3"</span>');
      return `<span class="tk-tag">${open}${name}</span>${a}<span class="tk-tag">${close}</span>`;
    });
    return s + '\n';
  }

  function CodeEditor({ value, onChange, apiRef, dirty, onFormat, readOnly }) {
    const taRef = useRef(null), hlRef = useRef(null), gutRef = useRef(null);
    const lines = value.split('\n').length;

    const sync = () => {
      const ta = taRef.current; if (!ta) return;
      const st = ta.scrollTop, sl = ta.scrollLeft;
      if (hlRef.current) hlRef.current.style.transform = `translate(${-sl}px, ${-st}px)`;
      if (gutRef.current) gutRef.current.style.transform = `translateY(${-st}px)`;
    };
    useEffect(sync, [value]);

    // expose insert-at-cursor
    useEffect(() => {
      if (!apiRef) return;
      apiRef.current = {
        insert(text) {
          const ta = taRef.current; if (!ta) { onChange(value + text); return; }
          const focused = document.activeElement === ta && (ta.selectionStart > 0 || ta.selectionEnd > 0);
          let s, e, ins = text;
          if (focused) { s = ta.selectionStart; e = ta.selectionEnd; }
          else { s = e = value.length; ins = (value.endsWith('\n') ? '' : '\n') + text; }
          const next = value.slice(0, s) + ins + value.slice(e);
          onChange(next);
          requestAnimationFrame(() => {
            ta.focus();
            ta.setSelectionRange(s, s + ins.length);   // highlight what we added
            if (!focused) ta.scrollTop = ta.scrollHeight;
            sync();
          });
        },
      };
    });

    const onKeyDown = (e) => {
      if (e.key === 'Tab') {
        e.preventDefault();
        const ta = e.target, s = ta.selectionStart, en = ta.selectionEnd;
        const next = value.slice(0, s) + '  ' + value.slice(en);
        onChange(next);
        requestAnimationFrame(() => ta.setSelectionRange(s + 2, s + 2));
      }
    };

    return (
      <div className="pb-editor-wrap">
        <div className="pb-gutter">
          <div className="pb-gutter-inner" ref={gutRef}>
            {Array.from({ length: lines }, (_, i) => <span key={i}>{i + 1}</span>)}
          </div>
        </div>
        <div className="pb-code-scroll">
          <pre className="pb-code-hl" ref={hlRef} dangerouslySetInnerHTML={{ __html: highlight(value) }} />
          <textarea className="pb-code-area" ref={taRef} value={value} spellCheck="false" readOnly={readOnly}
            onChange={(e) => onChange(e.target.value)} onScroll={sync} onKeyDown={onKeyDown} />
        </div>
      </div>
    );
  }

  // ── field tree ───────────────────────────────────────────
  function FieldGroup({ g, open, onToggle, onInsert }) {
    return (
      <div className="pb-fgroup">
        <button className={`pb-fgroup-h ${open ? 'is-open' : ''}`} onClick={onToggle}>
          <Ic k="chevR" className="pb-fchev" />
          <span className="pb-fgroup-ic"><Ic k={g.icon} /></span>
          {g.label}
          <span className="pb-fgroup-c">{g.fields.length}</span>
        </button>
        {open && (
          <div className="pb-fields">
            {g.fields.map((f, i) => (
              <div key={i} className={`pb-field ${f.loop ? 'pb-field--loop' : ''}`} onClick={() => onInsert(f.tag)} title={`Insert ${f.tag}`}>
                <span className="pb-field-tag">{f.tag.length > 26 ? f.tag.slice(0, 24) + '…' : f.tag}</span>
                <span className="pb-field-lbl">{f.label}</span>
                <span className="pb-field-ins"><Ic k="plus" /></span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  function FieldTree({ groups, onInsert }) {
    const [open, setOpen] = useState(() => { const o = {}; groups.forEach((g, i) => o[g.key] = i < 2); return o; });
    useEffect(() => { setOpen(o => { const n = { ...o }; groups.forEach((g, i) => { if (!(g.key in n)) n[g.key] = i < 2; }); return n; }); }, [groups]);
    return (
      <div>
        <div className="pb-fnote"><b>Advanced.</b> Click a field to drop its <code>{'{{'}placeholder{'}}'}</code> into the HTML at your cursor — it then shows that value on the page. Loops like <code>{'{{#each items}}'}</code> repeat a block per row.<br/><br/>To <b>show / hide</b> whole sections without code, use <b>Design ▸ Content</b>. To change a value, just click it on the document.</div>
        {groups.map(g => (
          <FieldGroup key={g.key} g={g} open={!!open[g.key]} onToggle={() => setOpen(o => ({ ...o, [g.key]: !o[g.key] }))} onInsert={onInsert} />
        ))}
      </div>
    );
  }

  export const PBEdit = { CodeEditor, FieldTree };

